import React from 'react';
import moment from 'moment';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';

const CustomAgendaView = ({ events, date, onSelectEvent }) => {
  // Get date range for agenda view (current month + next 2 months)
  const startDate = moment(date).startOf('month');
  const endDate = moment(date).endOf('month').add(2, 'months');
  
  // Filter and group events by date
  const filteredEvents = events.filter(event => {
    const eventDate = moment(event.start);
    return eventDate.isBetween(startDate, endDate, 'day', '[]');
  });

  // Group events by date
  const eventsByDate = filteredEvents.reduce((groups, event) => {
    const dateKey = moment(event.start).format('YYYY-MM-DD');
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(event);
    return groups;
  }, {});

  // Sort dates
  const sortedDates = Object.keys(eventsByDate).sort();

  if (sortedDates.length === 0) {
    return (
      <div className="p-8 text-center">
        <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Events Scheduled</h3>
        <p className="text-gray-500">No events found for the selected time period.</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-12 gap-4 px-6 py-3">
            <div className="col-span-3 font-semibold text-gray-700 text-sm">Date</div>
            <div className="col-span-2 font-semibold text-gray-700 text-sm">Time</div>
            <div className="col-span-7 font-semibold text-gray-700 text-sm">Event</div>
          </div>
        </div>

        {/* Events */}
        <div className="divide-y divide-gray-100">
          {sortedDates.map(dateKey => {
            const dateEvents = eventsByDate[dateKey];

            return (
              <div key={dateKey}>
                {dateEvents.map((event, eventIndex) => {
                  const isFirstEventOfDay = eventIndex === 0;
                  const startTime = moment(event.start).format('h:mm A');
                  const endTime = moment(event.end).format('h:mm A');
                  const duration = moment(event.end).diff(moment(event.start), 'minutes');
                  const isBlockedTime = event.resource?.type === 'blocked_time';
                  
                  return (
                    <div 
                      key={`${dateKey}-${event.id}`}
                      className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => onSelectEvent && onSelectEvent(event)}
                    >
                      {/* Date Column */}
                      <div className="col-span-3">
                        {isFirstEventOfDay && (
                          <div>
                            <div className="font-medium text-gray-900">
                              {moment(dateKey).format('dddd')}
                            </div>
                            <div className="text-sm text-gray-600">
                              {moment(dateKey).format('MMMM Do, YYYY')}
                            </div>
                            {moment(dateKey).isSame(moment(), 'day') && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                                Today
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Time Column */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-1 text-sm">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {startTime}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {duration >= 60 
                            ? `${Math.floor(duration / 60)}h ${duration % 60}m`
                            : `${duration}m`
                          }
                        </div>
                      </div>

                      {/* Event Column */}
                      <div className="col-span-7">
                        <div className="flex items-start space-x-3">
                          {/* Event type indicator */}
                          <div 
                            className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
                              isBlockedTime 
                                ? 'bg-red-400' 
                                : 'bg-blue-400'
                            }`}
                          />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-gray-900 truncate">
                                {event.title}
                              </h4>
                              {event.event_type && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  isBlockedTime
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {event.event_type.replace('_', ' ')}
                                </span>
                              )}
                            </div>
                            
                            {event.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {event.description}
                              </p>
                            )}
                            
                            {event.location && (
                              <p className="text-sm text-gray-500 mt-1">
                                📍 {event.location}
                              </p>
                            )}

                            {/* Additional event details */}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>{startTime} - {endTime}</span>
                              {event.external_calendar_syncs?.google && (
                                <span className="inline-flex items-center text-green-600">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Synced
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CustomAgendaView;